import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  updatePerformerProfile,
  updateUserLocation,
  addProfession,
  scanResume,
  addProfileCredit,
} from '../services/api';
import { locationOptions } from '../constants/locations';
import './Onboarding.css';

function Onboarding() {
  const { user, completeOnboarding } = useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Step 1: Basic info
  const [tagline, setTagline] = useState('');
  const [location, setLocation] = useState('');
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [customProfession, setCustomProfession] = useState('');

  // Step 2: Resume
  const [resumeFile, setResumeFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scannedCredits, setScannedCredits] = useState(null);
  const [scanError, setScanError] = useState('');

  const professionOptions = [
    'Actor',
    'Singer',
    'Dancer',
    'Stage Manager',
    'Director',
    'Choreographer',
    'Music Director',
    'Playwright',
    'Composer',
    'Casting Director',
    'Producer',
    'Lighting Designer',
    'Sound Designer',
    'Set Designer',
    'Costume Designer',
    'Dramaturg',
    'Stage Hand',
    'Fight Choreographer',
    'Intimacy Coordinator',
    'Voice Coach',
  ];

  const toggleProfession = (prof) => {
    setSelectedProfessions((prev) =>
      prev.includes(prof) ? prev.filter((p) => p !== prof) : [...prev, prof]
    );
  };

  const addCustomProfession = () => {
    if (customProfession.trim() && !selectedProfessions.includes(customProfession.trim())) {
      setSelectedProfessions((prev) => [...prev, customProfession.trim()]);
      setCustomProfession('');
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResumeFile(file);
    setScanning(true);
    setScanError('');
    try {
      const res = await scanResume(file);
      const credits = res.data?.credits || [];
      setScannedCredits(credits);
    } catch (err) {
      console.error('Resume scan error:', err);
      setScanError('Could not scan resume. You can add credits manually in your profile settings.');
    }
    setScanning(false);
  };

  const handleSaveStep1 = async () => {
    setSaving(true);
    try {
      const promises = [];
      if (tagline || location) {
        promises.push(updatePerformerProfile({ tagline }));
      }
      if (location) {
        promises.push(updateUserLocation(location));
      }
      for (const prof of selectedProfessions) {
        promises.push(addProfession({ profession: prof }));
      }
      await Promise.all(promises);
    } catch (err) {
      console.error('Error saving onboarding step 1:', err);
    }
    setSaving(false);
    setStep(2);
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      if (scannedCredits && scannedCredits.length > 0) {
        for (const credit of scannedCredits) {
          await addProfileCredit({
            category: credit.category || 'Theatre',
            role: credit.role || '',
            production: credit.production || '',
            company: credit.company || '',
            director: credit.director || '',
            year: credit.year || '',
          });
        }
      }
    } catch (err) {
      console.error('Error saving credits:', err);
    }
    const result = await completeOnboarding();
    if (!result.success) {
      console.error('Error completing onboarding:', result.error);
    }
    setSaving(false);
    navigate('/feed');
  };

  const handleSkip = async () => {
    const result = await completeOnboarding();
    if (!result.success) {
      console.error('Error completing onboarding:', result.error);
    }
    navigate('/feed');
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        <div className="onboarding-header">
          <div className="onboarding-brand">Showcase</div>
          <div className="onboarding-steps">
            <div className={`onboarding-step-dot ${step >= 1 ? 'active' : ''}`} />
            <div className="onboarding-step-line" />
            <div className={`onboarding-step-dot ${step >= 2 ? 'active' : ''}`} />
          </div>
          <button className="onboarding-skip" onClick={handleSkip}>
            Skip for now
          </button>
        </div>

        {step === 1 && (
          <div className="onboarding-step">
            <h1 className="onboarding-title">
              Welcome, {user?.full_name?.split(' ')[0] || 'there'}.
            </h1>
            <p className="onboarding-subtitle">
              Let's set up your profile so you can connect with people you've worked with.
            </p>

            <div className="onboarding-field">
              <label>What do you do?</label>
              <div className="onboarding-professions">
                {professionOptions.map((prof) => (
                  <button
                    key={prof}
                    className={`onboarding-profession-chip ${selectedProfessions.includes(prof) ? 'selected' : ''}`}
                    onClick={() => toggleProfession(prof)}
                  >
                    {prof}
                  </button>
                ))}
              </div>
              <div className="onboarding-custom-profession">
                <input
                  type="text"
                  placeholder="Other profession..."
                  value={customProfession}
                  onChange={(e) => setCustomProfession(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && (e.preventDefault(), addCustomProfession())
                  }
                />
                {customProfession.trim() && <button onClick={addCustomProfession}>Add</button>}
              </div>
            </div>

            <div className="onboarding-field">
              <label>Your tagline</label>
              <input
                type="text"
                className="onboarding-input"
                placeholder="e.g. Broadway actress & singer"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
              />
            </div>

            <div className="onboarding-field">
              <label>Where are you based?</label>
              <select
                className="onboarding-input"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              >
                <option value="">Select your location...</option>
                {locationOptions.map((group) => (
                  <optgroup key={group.group} label={group.group}>
                    {group.options.map((loc) => (
                      <option key={loc} value={loc}>
                        {loc}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="onboarding-actions">
              <button className="onboarding-next-btn" onClick={handleSaveStep1} disabled={saving}>
                {saving ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-step">
            <h1 className="onboarding-title">Upload your resume.</h1>
            <p className="onboarding-subtitle">
              We'll scan it to find your credits and connect you with people you've worked with.
              This powers your network suggestions.
            </p>

            <div className="onboarding-resume-upload">
              {!resumeFile ? (
                <label className="onboarding-resume-dropzone">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="12" y1="18" x2="12" y2="12" />
                    <polyline points="9 15 12 12 15 15" />
                  </svg>
                  <span className="onboarding-resume-text">
                    Drop your resume here or click to upload
                  </span>
                  <span className="onboarding-resume-formats">PDF, PNG, or JPEG</span>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    style={{ display: 'none' }}
                    onChange={handleResumeUpload}
                  />
                </label>
              ) : scanning ? (
                <div className="onboarding-scanning">
                  <div className="onboarding-spinner" />
                  <p>Scanning your resume...</p>
                  <p className="onboarding-scan-hint">This usually takes 10-15 seconds</p>
                </div>
              ) : scanError ? (
                <div className="onboarding-scan-result">
                  <p className="onboarding-scan-error">{scanError}</p>
                </div>
              ) : scannedCredits ? (
                <div className="onboarding-scan-result">
                  <div className="onboarding-scan-success">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#4ade80"
                      strokeWidth="2"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <span>
                      Found {scannedCredits.length} credit{scannedCredits.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="onboarding-credits-preview">
                    {scannedCredits.slice(0, 6).map((credit, i) => (
                      <div key={i} className="onboarding-credit-item">
                        <strong>{credit.role}</strong>
                        <span>
                          {credit.production}
                          {credit.company ? ` — ${credit.company}` : ''}
                        </span>
                      </div>
                    ))}
                    {scannedCredits.length > 6 && (
                      <p className="onboarding-credits-more">+{scannedCredits.length - 6} more</p>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="onboarding-actions">
              <button className="onboarding-next-btn" onClick={handleFinish} disabled={saving}>
                {saving
                  ? 'Saving...'
                  : scannedCredits?.length
                    ? 'Save Credits & Get Started'
                    : 'Get Started'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Onboarding;
